import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr, money } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Clock, Scissors, User, CalendarDays, ChevronLeft, CheckCircle2, Plus } from "lucide-react";

function nextDays(count) {
  const out = [];
  const base = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    out.push(d);
  }
  return out;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function Booking() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [barber, setBarber] = useState(null);
  const [day, setDay] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [addons, setAddons] = useState([]);

  const days = useMemo(() => nextDays(14), []);

  const totalDuration = (service?.duration || 0) + addons.reduce((s, a) => s + a.duration, 0);
  const totalPrice = (service?.price || 0) + addons.reduce((s, a) => s + a.price, 0);
  const toggleAddon = (s) =>
    setAddons((prev) => (prev.find((a) => a.id === s.id) ? prev.filter((a) => a.id !== s.id) : [...prev, s]));

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/services"), api.get("/barbers")]).then(([s, sv, b]) => {
      setSettings(s.data || {});
      setServices(sv.data || []);
      setBarbers(b.data || []);
      const psv = params.get("service");
      const pb = params.get("barber");
      if (psv) { const f = (sv.data || []).find((x) => x.id === psv); if (f) { setService(f); } }
      if (pb) { const f = (b.data || []).find((x) => x.id === pb); if (f) setBarber(f); }
    });
  }, [params]);

  useEffect(() => {
    if (step === 5 && service && barber && day) {
      setLoadingSlots(true);
      setSlot(null);
      const addon_ids = addons.map((a) => a.id).join(",");
      api.get("/availability", { params: { barber_id: barber.id, date: iso(day), service_id: service.id, addon_ids } })
        .then((r) => setSlots(r.data.slots || []))
        .catch(() => setSlots([]))
        .finally(() => setLoadingSlots(false));
    }
  }, [step, service, barber, day, addons]);

  const confirm = async () => {
    try {
      const { data } = await api.post("/appointments", {
        service_id: service.id, barber_id: barber.id, date: iso(day), start_time: slot,
        addon_ids: addons.map((a) => a.id),
      });
      setConfirmed(data);
      setStep(7);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Erro ao agendar");
    }
  };

  const steps = ["Serviço", "Adicionar", "Barbeiro", "Dia", "Horário", "Confirmar"];

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Navbar logo={settings.logo} shopName={settings.shop_name || "Big Black"} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {step <= 6 && (
          <>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Fazer Agendamento</h1>
            <p className="text-zinc-400 mb-8">Escolha o serviço, o barbeiro e o melhor horário para você.</p>
            <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2">
              {steps.map((s, i) => (
                <div key={s} className="flex items-center gap-2 shrink-0">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${step === i + 1 ? "bg-amber-400 text-black" : step > i + 1 ? "bg-amber-500/20 text-amber-400" : "bg-zinc-800 text-zinc-500"}`}>
                    {step > i + 1 ? <Check className="w-4 h-4" /> : <span>{i + 1}</span>} {s}
                  </div>
                  {i < steps.length - 1 && <div className="w-4 h-px bg-zinc-700" />}
                </div>
              ))}
            </div>
          </>
        )}

        {/* STEP 1 SERVICE */}
        {step === 1 && (
          <div data-testid="step-service">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <button key={s.id} onClick={() => { setService(s); setAddons((p) => p.filter((a) => a.id !== s.id)); setStep(2); }} className={`text-left p-5 rounded-xl border transition-colors ${service?.id === s.id ? "border-amber-400 bg-amber-500/10" : "border-zinc-800 bg-zinc-900 hover:border-amber-500/40"}`} data-testid={`pick-service-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2"><Scissors className="w-4 h-4 text-amber-400" />{s.name}</h3>
                    <span className="text-amber-400 font-bold">{money(s.price)}</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-2">{s.description}</p>
                  <span className="inline-flex items-center gap-1 text-zinc-500 text-sm mt-3"><Clock className="w-4 h-4" />{s.duration} min</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 ADD-ON SERVICES */}
        {step === 2 && service && (
          <div className="animate-fade-up" data-testid="addon-section">
            <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2"><Plus className="w-5 h-5 text-amber-400" /> Deseja adicionar mais algum serviço?</h3>
            <p className="text-sm text-zinc-400 mt-1 mb-4">Opcional — o tempo e o valor são somados automaticamente. Você pode pular esta etapa.</p>
            <div className="mb-4 p-4 rounded-xl border border-amber-400 bg-amber-500/10 flex items-center justify-between" data-testid="addon-main-service">
              <span className="flex items-center gap-2 text-white font-semibold"><Scissors className="w-4 h-4 text-amber-400" />{service.name} <span className="text-zinc-400 text-sm font-normal">(principal)</span></span>
              <span className="text-sm text-zinc-300">{money(service.price)} · {service.duration}min</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services.filter((s) => s.id !== service.id).map((s) => {
                const on = addons.find((a) => a.id === s.id);
                return (
                  <button key={s.id} onClick={() => toggleAddon(s)} className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${on ? "border-amber-400 bg-amber-500/10" : "border-zinc-800 bg-zinc-900 hover:border-amber-500/40"}`} data-testid={`pick-addon-${s.id}`}>
                    <span className="flex items-center gap-2 text-white font-medium">
                      <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${on ? "bg-amber-400 border-amber-400" : "border-zinc-600"}`}>{on && <Check className="w-3.5 h-3.5 text-black" />}</span>
                      {s.name}
                    </span>
                    <span className="text-sm text-zinc-400">+{money(s.price)} · {s.duration}min</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="text-zinc-300"><span className="text-zinc-500 text-sm">Total:</span> <span className="text-amber-400 font-bold text-lg">{money(totalPrice)}</span> <span className="text-zinc-500 text-sm">· {totalDuration} min</span></div>
              <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full px-8 h-12 hover:brightness-110" onClick={() => setStep(3)} data-testid="service-continue-btn">{addons.length ? "Continuar" : "Pular / Continuar"}</Button>
            </div>
          </div>
        )}

        {/* STEP 3 BARBER */}
        {step === 3 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="step-barber">
            {barbers.map((b) => (
              <button key={b.id} onClick={() => { setBarber(b); setStep(4); }} className={`p-5 rounded-xl border text-center transition-colors ${barber?.id === b.id ? "border-amber-400 bg-amber-500/10" : "border-zinc-800 bg-zinc-900 hover:border-amber-500/40"}`} data-testid={`pick-barber-${b.id}`}>
                {b.photo ? <img src={b.photo} alt={b.name} className="w-24 h-24 rounded-full object-cover mx-auto mb-3" /> : <div className="w-24 h-24 rounded-full pole-stripes mx-auto mb-3" />}
                <h3 className="font-heading font-bold text-white">{b.name}</h3>
                <p className="text-xs text-amber-400 mt-1">{b.role}</p>
              </button>
            ))}
          </div>
        )}

        {/* STEP 4 DAY */}
        {step === 4 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-3" data-testid="step-day">
            {days.map((d) => (
              <button key={iso(d)} onClick={() => { setDay(d); setStep(5); }} className={`p-4 rounded-xl border text-center transition-colors ${day && iso(day) === iso(d) ? "border-amber-400 bg-amber-500/10" : "border-zinc-800 bg-zinc-900 hover:border-amber-500/40"}`} data-testid={`pick-day-${iso(d)}`}>
                <div className="text-xs text-zinc-500">{WD[d.getDay()]}</div>
                <div className="text-xl font-bold text-white">{d.getDate()}</div>
                <div className="text-xs text-zinc-500">{d.toLocaleString("pt-BR", { month: "short" })}</div>
              </button>
            ))}
          </div>
        )}

        {/* STEP 5 SLOT */}
        {step === 5 && (
          <div data-testid="step-slot">
            {loadingSlots ? (
              <p className="text-zinc-400">Calculando horários disponíveis...</p>
            ) : slots.length === 0 ? (
              <div className="text-center py-10 max-w-md mx-auto" data-testid="no-slots-message">
                <p className="text-zinc-300">Este horário/dia não comporta a duração total do seu atendimento ({totalDuration} min). O período restante não tem tempo suficiente para concluir o serviço.</p>
                <p className="text-zinc-500 text-sm mt-2">Selecione outro dia ou remova um adicional para liberar mais horários.</p>
                <Button variant="outline" className="mt-4 border-amber-500/40 text-amber-100" onClick={() => setStep(4)}>Escolher outro dia</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {slots.map((t) => (
                    <button key={t} onClick={() => setSlot(t)} className={`py-3 rounded-lg border font-semibold transition-colors ${slot === t ? "border-amber-400 bg-amber-400 text-black" : "border-zinc-800 bg-zinc-900 text-white hover:border-amber-500/40"}`} data-testid={`pick-slot-${t}`}>{t}</button>
                  ))}
                </div>
                <Button disabled={!slot} className="mt-8 bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full px-8 h-12 hover:brightness-110" onClick={() => setStep(6)} data-testid="slot-continue-btn">Continuar</Button>
              </>
            )}
          </div>
        )}

        {/* STEP 6 SUMMARY */}
        {step === 6 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-lg mx-auto" data-testid="step-summary">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">Resumo do Agendamento</h2>
            <div className="space-y-4">
              <Row icon={Scissors} label="Serviço" value={`${service.name} • ${money(service.price)}`} />
              {addons.map((a) => (
                <Row key={a.id} icon={Plus} label="Adicional" value={`${a.name} • ${money(a.price)}`} />
              ))}
              <Row icon={User} label="Barbeiro" value={barber.name} />
              <Row icon={CalendarDays} label="Data" value={day.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })} />
              <Row icon={Clock} label="Horário" value={`${slot} — ${totalDuration} min`} />
              <div className="flex items-center justify-between pt-2">
                <span className="text-zinc-400 font-semibold">Total</span>
                <span className="text-amber-400 font-bold text-xl">{money(totalPrice)}</span>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setStep(5)} data-testid="summary-back-btn"><ChevronLeft className="w-4 h-4" /> Voltar</Button>
              <Button className="flex-1 bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full h-12 hover:brightness-110" onClick={confirm} data-testid="confirm-booking-btn">Confirmar Agendamento</Button>
            </div>
          </div>
        )}

        {/* STEP 7 SUCCESS */}
        {step === 7 && confirmed && (
          <div className="text-center py-16 animate-fade-up" data-testid="booking-success">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold text-white">Agendamento Confirmado!</h2>
            <p className="text-zinc-400 mt-3">Seu {confirmed.service_name} com {confirmed.barber_name} está marcado para<br />
              <span className="text-amber-400 font-semibold">{new Date(confirmed.date + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })} às {confirmed.start_time}</span>.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={() => nav("/meus-agendamentos")} data-testid="success-my-appointments-btn">Ver meus agendamentos</Button>
              <Button variant="outline" className="border-amber-500/40 text-amber-100" onClick={() => nav("/")}>Voltar ao início</Button>
            </div>
          </div>
        )}

        {step > 1 && step < 6 && (
          <Button variant="ghost" className="mt-8 text-zinc-500 hover:text-amber-400" onClick={() => setStep(step - 1)} data-testid="booking-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Voltar</Button>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 pb-4 border-b border-zinc-800">
      <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center"><Icon className="w-5 h-5 text-amber-400" /></div>
      <div>
        <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
        <div className="text-white font-semibold capitalize">{value}</div>
      </div>
    </div>
  );
}
